/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Box } from '@mui/material';
import React from 'react';
import { CustomToggleButton, CustomTooltip } from '@opengeoweb/shared';
import { WorkspaceMenu } from '.';
import { useWorkspaceTranslation } from '../../utils/i18n';

export interface WorkspaceMenuButtonProps {
  title: string;
  activeTab?: boolean;
  hasChanges?: boolean;
  id?: string;
  showMenu?: boolean;
  onToggleDialog?: (open: boolean) => void;
}

export const workspacePresetOptionId = 'workspaceMenuButton';
export const viewPresetOptionId = 'viewpreset-options-toolbutton';

export const WorkspaceMenuButton: React.FC<WorkspaceMenuButtonProps> = ({
  title,
  activeTab,
  hasChanges = false,
  id = workspacePresetOptionId,
  showMenu = false,
  onToggleDialog,
}: WorkspaceMenuButtonProps) => {
  const handleClick = (): void => {
    if (onToggleDialog) {
      onToggleDialog(!showMenu);
    }
  };

  const { t } = useWorkspaceTranslation();

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexGrow: 1,
        minWidth: 0,
      }}
    >
      <CustomTooltip
        slotProps={{
          transition: { exit: false },
        }}
        title={
          !showMenu && (
            <>
              <Box
                component="span"
                sx={{ fontWeight: 'bold', display: 'block' }}
              >
                {t('workspace-menu')}
              </Box>
              <Box component="span">{`${t('workspace-currently')}: ${title}`}</Box>
            </>
          )
        }
      >
        <CustomToggleButton
          aria-label="workspace menu"
          data-testid={id}
          disableRipple
          onClick={handleClick}
          selected={showMenu}
          sx={{
            flexGrow: 1, // expand across the row
            flexShrink: 1,
            minWidth: 0,
            justifyContent: 'flex-start',
            alignItems: 'center',
            textAlign: 'left',
            fontSize: '12px',
            fontWeight: 500,
            fontStyle: hasChanges ? 'italic' : undefined,
            height: 20,
            maxHeight: 20,
            overflow: 'hidden',
            '&&': {
              color:
                activeTab && !showMenu
                  ? 'geowebColors.buttons.icon.active.color'
                  : 'geowebColors.buttons.icon.active.color',
              textTransform: 'none',
              backgroundColor: !showMenu
                ? 'transparent'
                : 'geowebColors.workspace.tabButtonHover',
              '&:hover': {
                backgroundColor: 'geowebColors.workspace.tabButtonHover',
                color:
                  activeTab && showMenu
                    ? 'white'
                    : 'geowebColors.buttons.icon.active.color',
              },
            },
          }}
          variant="tool"
        >
          <Box
            component="span"
            sx={{
              flexGrow: 1,
              flexShrink: 1,
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              direction: 'ltr',
              textAlign: 'left',
            }}
          >
            {title}
          </Box>
        </CustomToggleButton>
      </CustomTooltip>

      <WorkspaceMenu
        closeMenu={() => onToggleDialog?.(false)}
        isOpen={showMenu}
      />
    </Box>
  );
};
